import { ReadonlyVec3 } from 'gl-matrix';
import type { DicomColor } from '~/domain/dicom/dicomColor';
import { toPointMm } from '~/domain/geometry/patientGeometry';
import {
    DisplayedPlanePlacement,
    displayedPlanePlacement,
} from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import type { UncommittedPointCoordinates } from '~/state/atoms/structureSet/roiAtoms';
import { dicomColorToRgbString } from '~/utils/color';
import {
    POINT_MARKER_FILL_ALPHA_OFF_PLANE,
    POINT_MARKER_FILL_ALPHA_ON_PLANE,
} from './pointMarkerGeometry';

/** The subset of an engine RegionOfInterest the projection reads. */
export interface PoiSource {
    readonly roiNumber: number;
    readonly name: string;
    readonly isVisible: boolean;
    /** True for point-type ROIs (MARKER, ISOCENTER, REGISTRATION). */
    readonly isPointRoi: boolean;
    /** Patient-space point in mm, or null before the point is placed. */
    readonly pointCoordinatesMm: ReadonlyVec3 | null;
    readonly colorRgb8: DicomColor;
}

/** An unsaved colour shown on one point ROI while its colour is edited. */
export interface PoiColorPreview {
    readonly roiNumber: number;
    readonly color: DicomColor;
}

/** One renderable POI marker in tile canvas CSS pixels. */
export interface TilePoiMarker {
    readonly roiNumber: number;
    readonly name: string;
    readonly colorCss: string;
    readonly fillCss: string;
    readonly canvasX: number;
    readonly canvasY: number;
    /**
     * Where the point lies relative to what the view shows
     * ({@link displayedPlanePlacement}); `offSlice` only for the selected
     * point.
     */
    readonly placement: DisplayedPlanePlacement;
}

/**
 * Projects the visible point-type ROIs of a structure set onto the
 * displayed plane. A point renders on its own slice only: the points in
 * the displayed slice are returned, and the one exception is the ROI
 * numbered `selectedRoiNumber`, returned wherever it lies, so the user sees
 * where the selected point lies relative to the slice. The selected marker
 * comes last, so it is drawn on top. Pass `selectedRoiNumber` only for the
 * structure set the selection belongs to. The ROI `colorPreview` names is
 * drawn in its unsaved colour, and the ROI `pointPreview` names at the
 * point a move in progress would place it; pass each, too, only for the
 * structure set the edit belongs to.
 */
export function projectPointRoisOntoPlane(
    view: PlaneView,
    rois: ReadonlyArray<PoiSource>,
    selectedRoiNumber?: number,
    colorPreview?: PoiColorPreview,
    pointPreview?: UncommittedPointCoordinates
): TilePoiMarker[] {
    const markers: TilePoiMarker[] = [];
    let selectedMarker: TilePoiMarker | undefined;
    for (const roi of rois) {
        const pointMm =
            pointPreview?.roiNumber === roi.roiNumber
                ? pointPreview.coordinatesMm
                : roi.pointCoordinatesMm;
        if (!roi.isPointRoi || !roi.isVisible || pointMm === null) {
            continue;
        }
        const isSelected = roi.roiNumber === selectedRoiNumber;
        const placement = displayedPlanePlacement(toPointMm(pointMm), view);
        if (placement === 'offSlice' && !isSelected) {
            continue;
        }
        const canvas = view.canvasCoordsFromPatientCoords(pointMm);
        const color =
            colorPreview?.roiNumber === roi.roiNumber
                ? colorPreview.color
                : roi.colorRgb8;
        const { R, G, B } = color;
        const marker: TilePoiMarker = {
            roiNumber: roi.roiNumber,
            name: roi.name,
            colorCss: dicomColorToRgbString(color),
            fillCss: `rgba(${R}, ${G}, ${B}, ${placement === 'onPlane' ? POINT_MARKER_FILL_ALPHA_ON_PLANE : POINT_MARKER_FILL_ALPHA_OFF_PLANE})`,
            canvasX: canvas[0],
            canvasY: canvas[1],
            placement,
        };
        if (isSelected) {
            selectedMarker = marker;
        } else {
            markers.push(marker);
        }
    }
    return selectedMarker ? [...markers, selectedMarker] : markers;
}
